import Game from 'Game';
import IPlayerData, { playerDataKeysToLoad } from 'Player/IPlayerData';
import { IAuthReturnValues } from 'State/Game/Action/SignIn';
import { IMessagePayload } from 'State/Game/IEntryPointData';
import { IGameState } from 'State/Game/IGameState';
import { Leaderboard } from 'System/UI/LeaderboardHelper';
import { AuthPlatform } from '@softgames/mobile-dev-tools';
import { FBInstantSaver, GameBackendSaver, LocalStorageSaver, Privileges, SaveHandler } from '@softgames/saver';
import { getResolvedPromise, sleep, timeoutPromise } from '../../async';
import DateHelper, { setServerDate } from '../../DateHelper';
import { Deferred } from '../../Deferred';
import { decryptPayload } from '../../EntryPoint/EntryPointEncryption';
import { EncryptedEntryPoint } from '../../EntryPoint/EntryPointHelper';
import { EntryPointType } from '../../EntryPoint/EntryPointType';
import { LoadEvents, LoadEventType, LoadtimeTrackerSingleton } from '../../LoadtimeTracker';
import { isIOS } from '../../Platform';
import { Random } from '../../Random';
import { getLatestData, handleSaverError, saveHandlerLogger } from '../../SaveHandlers/SaveHandlerHelper';
import { AdType } from '../../Tracking/DataSet/Ad';
import { SocialActionTypes } from '../../Tracking/DataSet/SocialAction';
import { FBTrackingEvent } from '../../Tracking/FBTrackingEvent';
import { track } from '../../Tracking/Track';
import { trackDisplayedAdvertisement, trackRequestAdvertisement } from '../../Tracking/TrackingHelpers/Ad';
import { AdvertisementType, IAdvertisementPlacement } from '../Advertisement';
import { GBCHandler } from '../GBCHandler';
import { defaultLanguageKey, LanguageKey } from '../Language';
import PayloadHelper from '../PayloadHelper';
import { PayloadType } from '../PayloadType';
import { FBContextHandler } from './FBContextHandler';
import { getFBInstant, isAPISupported } from './FBHelpers';
import { FBPayments } from './FBPayments';
import { IScoreEntry } from './IScoreEntry';
import ISDKDriver from './ISDKDriver';
import { getRewardedVideo } from 'Utility/SDK/Drivers/GetRewardedVideo';
import { IShowable } from 'Utility/SDK/Drivers/IShowable';
import { connectToGBC } from '../GBC/ConnectToGBC';
import { report } from 'report';
import { isDataSetEmpty, PlayerDataLocation } from 'Utility/DataConflict/DataConflictsHelper';
import GameStateHelper from 'State/Game/GameStateHelper';
import { Logger } from 'Utility/Logger/Logger';
import { LogOrigin } from 'Utility/Logger/LogOrigin';
import { AvoTracking } from 'Utility/Tracking/Avo/AvoTracking';

declare const FBInstant: any;

declare global {
    interface Window {
        getUserId: () => string;
    }
}

const A2UTimes: Map<string, number> = new Map<string, number>([
    //['now', 0],
    ['5h', DateHelper.Hour * 5],
    ['24h', DateHelper.Day],
    ['48h', DateHelper.Day * 2],
    ['72h', DateHelper.Day * 3],
]);

export default class FBSDKDriver implements ISDKDriver {
    private A2UClient: any;

    private user: {
        id: string;
        name: string;
        avatar: string;
    };
    private gbUserId: string;
    private readonly gbUserProfile = new Deferred<GBCHandler.IGBUserProfile>();
    private settings: {
        locale: string;
        platform: string;
        sdkVersion: string;
    };
    private entryPoint: any;

    private _isSubscribedToBot: boolean;

    public readonly initialized: Promise<void>;

    private readonly botMessageId = new Deferred<string>();

    private readonly fbPayments = new FBPayments();

    private saveHandler: SaveHandler<IPlayerData>;
    private _debugGBSaver: GameBackendSaver<IPlayerData>;

    constructor() {
        this.debugExpose();
        this.initialized = this.init();
    }

    private debugExpose() {
        if (!window) return;
        window.getUserId = () => this.getUserId();
    }

    private async init() {
        LoadtimeTrackerSingleton.trackLoadTask(LoadEvents.FB_preStartGame, LoadEventType.Start);
        await getFBInstant();
        await FBInstant.initializeAsync();

        this.saveHandler = new SaveHandler({
            drivers: [
                new LocalStorageSaver({
                    getUserId: FBInstant.player.getID,
                    saveDataKey: 'sg_sofa4_data',
                }),
                new FBInstantSaver({
                    setDataAsync: FBInstant.player.setDataAsync,
                    flushDataAsync: FBInstant.player.flushDataAsync,
                    getDataAsync: FBInstant.player.getDataAsync,
                    saveDataKey: 'sg_sofa4_data',
                }),
            ],
            logger: saveHandlerLogger,
            getLatestData: getLatestData,
            handleError: handleSaverError,
        });

        console.info('FBInstant Initialized');
        this.gameTracking(FBTrackingEvent.FTUE_00_FBInit);

        const serverTime = await this.getServerTimestamp();
        setServerDate(serverTime);

        this.settings = {
            locale: this.parseLocale(FBInstant.getLocale()),
            platform: FBInstant.getPlatform(),
            sdkVersion: FBInstant.getSDKVersion(),
        };
        this.user = {
            id: await this.waitForUserId(FBInstant.player),
            name: FBInstant.player.getName(),
            avatar: FBInstant.player.getPhoto(),
        };

        this.hideConnectingNotice();

        this.saveHandler.setSavePrivileges(Privileges.PERMITTED);

        LoadtimeTrackerSingleton.trackLoadTask(LoadEvents.FB_preStartGame, LoadEventType.End);
    }

    private async waitForUserId(player: typeof FBInstant.player): Promise<string> {
        let userId: string;
        if (await timeoutPromise(10000, getUserId(player))) {
            return userId;
        }
        report('UserId not found/timed out', {
            once: true,
            sampleRate: 0.1,
            fingerprint: 'FBSDKDriver:waitForUserId:FailedToGetUserId',
        });
        return undefined;

        async function getUserId(player: any): Promise<void> {
            while (!player.getID()) {
                await sleep(1);
            }
            userId = player.getID();
        }
    }

    private parseLocale(locale: string): string {
        if (locale === 'pt_BR') {
            return LanguageKey.BRAZILIAN;
        }
        return locale.substr(0, 2);
    }

    private async getServerTimestamp(): Promise<number> {
        return Date.now();
    }

    private hideConnectingNotice() {
        if (!document) return;
        const element = document.getElementById('gbMsg');
        if (!element) return;
        element.style.visibility = 'hidden';
    }

    public async connectToGBC() {
        const isConnected = await GBCHandler.isConnected();
        if (!isConnected) {
            const AUTH_DATA_KEY = 'authData';

            const gameId = Game.config.gameID;
            console.log(`Game ID: ${gameId}`);
            try {
                const authData = await GBCHandler.login({
                    // FIXME: use correct gameID for dev environment
                    // registering with prod gameID in dev environment will fail
                    // and all the tracking will fail due to missing userID
                    gameId,
                    cacheRead: () => readKeyFromFB(AUTH_DATA_KEY),
                    cacheWrite: data => saveKeyToFB(AUTH_DATA_KEY, data, this),
                    register: async () => {
                        // TODO: cache between retries (not sessions)?
                        const playerInfo = await FBInstant.player.getSignedPlayerInfoAsync();
                        const signature = playerInfo.getSignature();
                        return await GBCHandler.registerWithFacebookInstantGameSignature({
                            gameId,
                            signature,
                        });
                    },
                });
                this.gbUserId = authData.userId;
            } catch (error) {
                return Promise.reject(error);
            }
        } else {
            report('Attempting to reconnect to GBC while already connected', {
                once: true,
                sampleRate: 0.1,
                fingerprint: 'FBSDK:ConnectToGBC:AlreadyConnected',
            });
        }

        await GBCHandler.onLogin({
            syncFriendsList: this.syncFriendsList,
            userProfile: this.gbUserProfile,
            botMessageId: this.botMessageId,
        });

        return;

        async function saveKeyToFB(key: string, value: unknown, ctx: FBSDKDriver) {
            await ctx.saveFB({ [key]: value });
        }

        async function readKeyFromFB<T = unknown>(key: string): Promise<T | undefined> {
            try {
                const data = await FBInstant.player.getDataAsync([key]);
                return data[key];
            } catch (e) {
                return undefined;
            }
        }
    }

    private async syncFriendsList() {
        const friendList: string[] = await Game.sdk.getFriends();

        GBCHandler.syncFriendListByPlatformPlayerId({ friendList });
    }

    public async getFriends(): Promise<string[]> {
        const fbFriends: Array<{
            getID(): string;
        }> = await FBInstant.player.getConnectedPlayersAsync();
        const friendList = fbFriends.map(friendData => friendData.getID());
        return friendList;
    }

    public async save(data: Partial<IPlayerData>, saveToGBC: boolean = true) {
        Logger.log('Saving to sdk', LogOrigin.FBSDK);
        return this.saveHandler.save(data);
    }

    public async flushSaveData(): Promise<unknown> {
        // Handled by FBInstantSaver
        return;
    }

    public async restore(): Promise<IPlayerData> {
        const fbPlayerData = await this.restoreFB();
        return fbPlayerData;
    }

    // EMPTY - XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
    public async levelStart(level: number) { }

    // EMPTY - XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
    public async levelFinish(level: number, score: number, complete: boolean) { }

    // EMPTY - XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
    public async playWithFriends(text: string, imageBase64: string, payload: any, friendId?: string) { }

    public async customUpdate(
        text: string,
        imageBase64: string,
        friendId: string,
        payload: any,
        strategy?: string,
        cta?: string
    ) {
        const customMessage = await FBInstant.updateAsync({
            action: 'CUSTOM',
            cta: cta || 'Play',
            image: imageBase64,
            text: {
                default: text,
                localizations: {
                    en_US: text,
                },
            },
            template: 'default',
            data: payload,
            strategy: strategy || 'IMMEDIATE',
        });

        return customMessage;
    }

    public async loadComplete() {
        await FBInstant.startGameAsync();
        const handle = FBInstant.player.getName();

        /**
         * FIXME: This locale is not safe and can cause a validation error when logging into the GBC
         *      - [once] [0.1 sample] setUserProfile failed: Validation error: Validation is on locale failed
         */
        const locale = this.parseLocale(FBInstant.getLocale());

        if (this.settings.locale !== locale) {
            this.settings.locale = locale;
        }

        this.gbUserProfile.control.resolve({
            handle: handle || '',
            locale,
        });

        Logger.log('Game started.', LogOrigin.FBSDK);
        track('sdkVersion', FBInstant.getSDKVersion());

        await FBContextHandler.initialize(
            FBInstant.context.getID,
            FBInstant.context.getPlayersAsync,
            FBInstant.context.getType,
            FBInstant.context.chooseAsync,
            FBInstant.context.createAsync
        );

        // TODO: getDecryptedEntryPointData will return the decrypted contents of `entryPoint.enc` if the property `enc`
        //  exists (note this is not `entryPoint.payload.enc`) however this may not conform to the expected entryPoint format.
        const entryPointData = await this.getDecryptedEntryPointData();

        if (entryPointData) {
            this.setEntryPointData(entryPointData);

            // Bot message conversion tracking via GBC.
            if (entryPointData.entryPoint === EntryPointType.BOT && entryPointData.messageId) {
                this.botMessageId.control.resolve(entryPointData.messageId);
            }
        }

        track('customEntryPoint', (entryPointData && entryPointData.customEntryPoint) || 'ORGANIC');

        const contextType = FBContextHandler.getContextType();
        if (contextType) track('epContext', contextType);

        try {
            const entryPointAsync = await FBInstant.getEntryPointAsync();
            if (entryPointAsync) {
                track('entryPoint', entryPointAsync);
            }
        }
        catch (e) {
            //captureException(e);
        }
    }

    private async getDecryptedEntryPointData(): Promise<any> {
        const entryPointData = FBInstant.getEntryPointData();

        if (!entryPointData) {
            return null;
        }

        return entryPointData;
    }

    private async setEntryPointData(data: any) {
        if (data === null) return;
        this.entryPoint = data;

        // If the entry point has a sender, track the sender
        if (this.entryPoint.userId != undefined) {
            track('epSenderId', this.entryPoint.userId);

            // If the current user is not the sender, get the senders progress
            if (this.entryPoint.userId !== this.getUserId()) {
                this.getFriendsScores(Leaderboard.TOTAL_LEVELS, 'default').then(entries => {
                    const playerEntry: IScoreEntry = entries.find(
                        (entry: IScoreEntry) => entry.user.playerId === this.entryPoint.userId
                    );

                    if (playerEntry == undefined) return;

                    track('epSenderLevel', playerEntry.score);
                    this.entryPoint.userScore = playerEntry.score;
                });
            }
        }

        if (this.entryPoint.timestamp) {
            track('epTimestamp', this.entryPoint.timestamp);
        }
    }

    public getAvatar(): string {
        if (this.user && this.user.avatar) {
            return this.user.avatar;
        }

        return 'resource/graphics/other/no_avatar.png';
    }

    public getEntryPointData(): string {
        return this.entryPoint;
    }

    public getContextType(): string {
        return FBContextHandler.getContextType();
    }

    public getContextID(): string {
        return FBContextHandler.getContextID();
    }

    public isPlayerInContext(id: string): boolean {
        return FBContextHandler.isPlayerInContext(id);
    }

    public getUserId(): string {
        if (!this.user || !this.user.id) {
            // throw new Error('User ID not found.');
        }
        return this.user.id;
    }

    public getGBUserId(): string {
        if (!this.gbUserId) {
            // console.warn('User ID not found.');
        }
        return this.gbUserId;
    }

    public getUsername(): string {
        if (!this.user || !this.user.name) {
            return 'Unknown';
        }
        return this.user.name;
    }

    public async loadingProgress(progress: number) {
        progress = Math.floor(progress);
        if (progress >= 99) progress = 100;

        FBInstant.setLoadingProgress(progress);
    }

    // DIRTY - XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
    // private handler: any;
    public async onStart() {
        //this.handler.modules.start();
    }

    public async runQuery(query: string, params: any) {
        console.warn("'runQuery' is depreciated");
    }

    // DIRTY - XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
    public runGame(): void {
        //Game.start();
    }

    public freezeGame(): void {
        Game.ticker.stop();
    }

    public unfreezeGame(): void {
        Game.ticker.start();
    }

    public async getFriendsScores(leaderboardName: string, instanceId: string) {
        const leaderboardEntries = await this.fetchScores(leaderboardName, instanceId);

        const friendScores: IScoreEntry[] = leaderboardEntries.map(entry => {
            const scoreEntry: IScoreEntry = {
                ...entry,
                score: Number(entry.score),
                rank: Number(entry.rank),
                user: {
                    ...entry.user,
                    handle: entry.user.handle ? entry.user.handle.toString() : undefined,
                    playerId: entry.user.playerId ? entry.user.playerId.toString() : undefined,
                    avatarUrl: entry.user.avatarUrl ? entry.user.avatarUrl.toString() : undefined,
                    isCurrentUser: Boolean(entry.user.isCurrentUser),
                    id: entry.user.id.toString(),
                },
            };
            return scoreEntry;
        });

        return friendScores;
    }

    private async fetchScores(leaderboardName: string, instanceId: string) {
        try {
            const friends = await FBInstant.player.getConnectedPlayersAsync();

            const leaderboardEntries = await GBCHandler.fetchLeaderboard(leaderboardName, instanceId);

            const usersByPlayerId = [
                this.castPlayerToUser(FBInstant.player),
                ...friends.map(this.castPlayerToUser),
            ].reduce((m, user) => {
                return {
                    ...m,
                    [user.id]: user,
                };
            }, {} as Record<string, FBInstant.Player>);

            const decoratedResults = leaderboardEntries.map(entry => {
                const { user: entryUser } = entry;
                const { playerId, handle } = entryUser;
                const userData: any = usersByPlayerId[String(playerId)] || {};
                return {
                    ...entry,
                    user: {
                        ...entryUser,
                        handle: userData.name || handle || 'N/A',
                        avatarUrl: userData.avatarUrl,
                        isCurrentUser: playerId === this.getUserId(),
                        playerId,
                    },
                };
            });
            return decoratedResults;
        } catch (error) {
            console.error('Failed to fetch scores', error);
            return [];
        }
    }

    private castPlayerToUser(player: any) {
        return {
            id: player.getID(),
            name: player.getName(),
            avatarUrl: player.getPhoto(),
        };
    }

    public async share(text: string, imageBase64: string, payload?: IMessagePayload, intent?: FBInstant.Intent) {
        if (isIOS()) {
            const base64Image = imageBase64 || (await PayloadHelper.getInviteFriendBase64());
            const playerId: string = await Game.sdk.chooseFriend();
            return await Game.sdk.customUpdate(
                Game.locale.getText('InviteMessage'),
                base64Image,
                playerId,
                {
                    data: PayloadType.INVITE_FRIEND,
                    messageId: Random.ID(),
                    userId: Game.sdk.getUserId(),
                    userName: Game.sdk.getUsername(),
                    userAvatar: Game.sdk.getAvatar(),
                    customEntryPoint: SocialActionTypes.CUSTOM_INVITE_FRIEND,
                },
                'IMMEDIATE',
                Game.locale.getText('InviteCTA')
            );
        } else {
            return await FBInstant.shareAsync({
                intent: intent,
                image: imageBase64,
                text: text,
                data: payload,
            });
        }
    }

    public async createContext(playerId: string): Promise<string> {
        const players = FBContextHandler.getPlayers();
        const playerInContext: boolean = players.some((p: any) => {
            return p === playerId;
        });
        if (!playerInContext) {
            await FBContextHandler.createContextAsync(playerId);
        } else {
            console.log('Player is already in the current context');
        }

        return FBContextHandler.getContextID();
    }

    public async clearPlayerData() {
        return this.saveHandler.clear();
    }

    private async getAdvertisementInstance(placement: IAdvertisementPlacement, state: IGameState): Promise<IShowable> {
        const interstitialPerAdvertisementType: {
            [key in typeof placement['type']]: boolean;
        } = {
            [AdvertisementType.INTERSTITIAL]: true,
            [AdvertisementType.REWARDED]: false,
            [AdvertisementType.WATCH_TO_PLAY]: false,
        };

        const interstitial = interstitialPerAdvertisementType[placement.type];
        const debugFakeAds = false;

        const instance: IShowable = debugFakeAds
            ? FBSDKDriver.debugCreateFakeAd(placement.id)
            : interstitial
                ? await FBSDKDriver.getInterstitialAd(placement.id)
                : await getRewardedVideo(placement.id, state);

        track('adSuccess', Number(Boolean(instance)));

        await instance.loadAsync();

        return instance;
    }

    private static async getInterstitialAd(placementId: string): Promise<IShowable> {
        track('adType', AdType.INTERSTITIAL);
        return await FBInstant.getInterstitialAdAsync(placementId);
    }

    private static debugCreateFakeAd(placementId: string): IShowable {
        // Override ad tracking.
        track('adType', AdType.FAKE_AD);
        const logPrefix = '[Fake ads]';
        return {
            loadAsync: async () => {
                console.log(`${logPrefix} loadAsync start ${placementId}`);
                await sleep(200);
                console.log(`${logPrefix} loadAsync end ${placementId}`);
            },
            showAsync: async () => {
                console.log(`${logPrefix} showAsync start ${placementId}`);
                // WARNING: This does not prevent user from clicking stuff like real ads would do
                await sleep(3000);
                console.log(`${logPrefix} showAsync end ${placementId}`);
            },
        };
    }

    public async displayAdvertisement(state: IGameState, placement: IAdvertisementPlacement, payload: any) {
        const adId = Random.ID();
        const requestStartTime: number = DateHelper.now.getTime();
        payload.adTimestamp = requestStartTime;

        let instance: IShowable;
        try {
            instance = await this.getAdvertisementInstance(placement, state);
        } catch (e) {
            console.warn('Failed to get ad instance', e);
            throw e;
        }

        const requestEndTime: number = DateHelper.now.getTime();
        track('adRequestTimeMs', requestEndTime - requestStartTime);

        if (!instance) throw new Error('Unable to load advertisement.');

        try {
            console.log('Show ad');
            await instance.showAsync();
        } catch (e) {
            console.warn('Failed to show ad instance', e);
            payload.error = e.message;
            throw e;
        }
    }

    // Bot

    public async canSubscribeBot() {
        try {
            return FBInstant.player.canSubscribeBotAsync();
        } catch (e) {
            if (e.message === 'User is already subscribed to this game bot.') {
                this._isSubscribedToBot = true;
            } else {
                console.warn('canSubscribeBotAsync error:', e);
            }
            return false;
        }
    }
    public isSubscribedToBot(): boolean {
        return this._isSubscribedToBot;
    }

    public async subscribeBot() {
        return FBInstant.player
            .subscribeBotAsync()
            .then(() => {
                this._isSubscribedToBot = true;
                return true;
            })
            .catch(() => false);
    }

    //

    public getLanguage(): LanguageKey {
        if (!Object.values(LanguageKey).includes(this.settings.locale as any)) {
            console.warn(
                `Locale "${this.settings.locale}" is not yet supported, ` + `falling back to "${defaultLanguageKey}"`
            );
            return defaultLanguageKey;
        }
        return this.settings.locale as LanguageKey;
    }

    // API testing

    public hasRewardedAdSupport = () => isAPISupported('getRewardedVideoAsync');
    public hasInterstitialAdSupport = () => isAPISupported('getInterstitialAdAsync');
    public canSwitchGame = () => isAPISupported('startGameAsync');

    //

    public async switchGame(appID: string, payload: any) {
        return FBInstant.switchGameAsync(appID, {
            originGameId: Game.config.gameID,
            originPlayerId: this.getUserId(),
            promoReward: false,
            ...payload,
        });
    }

    private saveFB(data: Partial<IPlayerData>): Promise<void> {
        return this.saveHandler.save(data);
    }

    private readonly saveGBC = (data: Partial<IPlayerData>): Promise<void> => {
        const state = Game.store.getState();
        if (GameStateHelper.isGBCConnectionFullyResolved(state)) {
            return GBCHandler.saveToGBC(data);
        }
        return getResolvedPromise();
    };

    private readonly clearGBC = (): Promise<void> => {
        return new Promise((res, rej) => {
            GBCHandler.removeDataByKey(playerDataKeysToLoad)
                .then(() => {
                    Logger.log('Cleared GBC data.', LogOrigin.GB_SAVER);
                    res();
                })
                .catch(error => rej(error));
        });
    };

    private restoreFB(): Promise<IPlayerData> {
        return this.saveHandler.load(playerDataKeysToLoad) as Promise<IPlayerData>;
    }

    private restoreGB(): Promise<IPlayerData> {
        return GBCHandler.loadFromGBC(playerDataKeysToLoad);
    }

    public async chooseFriend(): Promise<string> {
        await FBContextHandler.chooseAsync();

        // Sam: I hate that we return the ID here
        return FBContextHandler.getContextID();
    }

    // Payments

    public readonly hasIAPSupport = () => false;
    public readonly purchaseProduct = (productId: string) => {
        return this.fbPayments.purchaseProduct(productId);
    };
    public readonly fetchCatalog = () => this.fbPayments.fetchCatalog();
    public readonly getCatalog = () => this.fbPayments.iapCatalog;
    public readonly waitForPaymentsEnabled = () => this.fbPayments.waitForPaymentsEnabled();
    public readonly resolveUnconsumedPurchases = () => this.fbPayments.resolveUnconsumedPurchases();
    public readonly getPurchases = () => this.fbPayments.getPurchases();

    public consumePurchase(product: FBInstant.Purchase): Promise<boolean> {
        return this.fbPayments.consumePurchase(product);
    }

    // Shortcut

    public async canCreateShortcut() {
        return FBInstant.canCreateShortcutAsync();
    }

    public async createShortcut() {
        return FBInstant.createShortcutAsync();
    }

    // Tracking

    public gameTracking(
        event: FBTrackingEvent,
        value: string | number = null,
        parameters: { [key: string]: string } = null
    ): void {
    }

    public async signIn(platform: AuthPlatform, silent: boolean): Promise<IAuthReturnValues> {
        return;
    }

    public signOut(): Promise<void> {
        return;
    }

    public getFacebookAuthToken(): string {
        return '';
    }
    public getFacebookAppScopedId(): string {
        return '';
    }
    public isFacebookConnected(): boolean {
        return false;
    }

    public getCDNUrl(): string {
        return `${Game.config.CDN_URL}/`;
    }
}
